import { create } from 'zustand';

import {
  calculateSystemPreview,
  getTicketSlipResolvedType,
  hasActiveSystemRows,
  syncSystemRows,
} from 'shared/utils/ticket-slip-helper';
import type { SystemBetRow, TicketBetMode, TicketStakeMode, TicketTip } from './types';

export type TicketSlipState = {
  ticket_slip: TicketTip[];
  total_stake: number;
  bet_mode: TicketBetMode;
  stake_mode: TicketStakeMode;
  system_rows: SystemBetRow[];
  loading: boolean;
  error: unknown | null;
  betslipTab: 'slip' | 'mybets';
  /** Set when switching to My Bets after placing a bet; the matching ticket is auto-expanded, then cleared. */
  newlyPlacedTicketId: number | null;

  addTip: (tip: TicketTip) => void;
  removeTip: (outcomeId: TicketTip['outcome_id']) => void;
  removeAllTips: () => void;
  updateTip: (patch: Partial<TicketTip> & { outcome_id: TicketTip['outcome_id'] }) => void;
  setTips: (tips: TicketTip[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: unknown | null) => void;
  setTotalStake: (totalStake: number) => void;
  setBetMode: (betMode: TicketBetMode) => void;
  setSystemRowStake: (size: number, stakePerLine: number) => void;
  setBetslipTab: (tab: 'slip' | 'mybets') => void;
  setNewlyPlacedTicketId: (id: number | null) => void;
  recomputeTotalStake: () => void;
  pruneTipsByEventIds: (eventIds: Array<string | number>) => void;
};

function sumStakes(tips: TicketTip[]): number {
  return tips
    .map((t) => Number(t.stake || 0))
    .reduce((a, b) => a + b, 0);
}

function normalizeTipStakes(args: {
  tips: TicketTip[];
  betMode: TicketBetMode;
  stakeMode: TicketStakeMode;
  systemRows: SystemBetRow[];
}): TicketTip[] {
  const resolvedType = getTicketSlipResolvedType(
    args.tips,
    args.betMode,
    args.systemRows,
    args.stakeMode,
  );
  if (resolvedType === 'DUTCHING') {
    return args.tips.map((tip) => {
      if (tip.stake == null) {
        return { ...tip, stake: 1 };
      }
      const stake = Number(tip.stake);
      return Number.isFinite(stake) && stake >= 0 ? tip : { ...tip, stake: 1 };
    });
  }

  return args.tips;
}

function deriveTotalStake(args: {
  tips: TicketTip[];
  betMode: TicketBetMode;
  stakeMode: TicketStakeMode;
  systemRows: SystemBetRow[];
  previousTotalStake: number;
}): number {
  const resolvedType = getTicketSlipResolvedType(
    args.tips,
    args.betMode,
    args.systemRows,
    args.stakeMode,
  );
  if (resolvedType === 'SYSTEM') {
    return calculateSystemPreview(args.tips, args.systemRows).totalStake;
  }

  if (resolvedType === 'DUTCHING') {
    return sumStakes(args.tips);
  }

  return args.tips.length === 0 ? 0 : args.previousTotalStake;
}

export const useTicketSlipStore = create<TicketSlipState>((set, get) => ({
  ticket_slip: [],
  total_stake: 0,
  bet_mode: 'STANDARD',
  stake_mode: 'total',
  system_rows: [],
  loading: false,
  error: null,
  betslipTab: 'slip',
  newlyPlacedTicketId: null,

  addTip: (tip) => {
    set((state) => {
      const nextRaw = state.ticket_slip.concat(tip);
      const nextSystemRows = syncSystemRows(nextRaw, state.system_rows);
      const nextBetMode = hasActiveSystemRows(nextSystemRows) ? 'SYSTEM' : 'STANDARD';
      const nextStakeMode: TicketStakeMode =
        nextRaw.length <= 1 ? 'total' : state.stake_mode;
      const next = normalizeTipStakes({
        tips: nextRaw,
        betMode: nextBetMode,
        stakeMode: nextStakeMode,
        systemRows: nextSystemRows,
      });
      const nextTotalStake =
        state.ticket_slip.length === 0
          ? Number(tip.stake) > 0
            ? Number(tip.stake)
            : 1
          : state.total_stake;
      return {
        ...state,
        ticket_slip: next,
        bet_mode: nextBetMode,
        stake_mode: nextStakeMode,
        system_rows: nextSystemRows,
        total_stake: deriveTotalStake({
          tips: next,
          betMode: nextBetMode,
          stakeMode: nextStakeMode,
          systemRows: nextSystemRows,
          previousTotalStake: nextTotalStake,
        }),
      };
    });
  },

  removeTip: (outcomeId) => {
    set((state) => {
      const nextRaw = state.ticket_slip.filter((tip) => tip.outcome_id !== outcomeId);
      const nextSystemRows = syncSystemRows(nextRaw, state.system_rows);
      const nextBetMode = hasActiveSystemRows(nextSystemRows) ? 'SYSTEM' : 'STANDARD';
      const nextStakeMode: TicketStakeMode =
        nextRaw.length <= 1 ? 'total' : state.stake_mode;
      const next = normalizeTipStakes({
        tips: nextRaw,
        betMode: nextBetMode,
        stakeMode: nextStakeMode,
        systemRows: nextSystemRows,
      });
      return {
        ...state,
        ticket_slip: next,
        bet_mode: nextBetMode,
        stake_mode: nextStakeMode,
        system_rows: nextSystemRows,
        total_stake: deriveTotalStake({
          tips: next,
          betMode: nextBetMode,
          stakeMode: nextStakeMode,
          systemRows: nextSystemRows,
          previousTotalStake: state.total_stake,
        }),
      };
    });
  },

  removeAllTips: () => {
    set((state) => ({
      ...state,
      ticket_slip: [],
      total_stake: 0,
      bet_mode: 'STANDARD',
      stake_mode: 'total',
      system_rows: [],
    }));
  },

  updateTip: (patch) => {
    set((state) => {
      const nextRaw = state.ticket_slip.map((tip) =>
        tip.outcome_id === patch.outcome_id ? { ...tip, ...patch } : tip,
      );
      const nextSystemRows = syncSystemRows(nextRaw, state.system_rows);
      const nextBetMode = hasActiveSystemRows(nextSystemRows) ? 'SYSTEM' : 'STANDARD';
      const nextStakeMode: TicketStakeMode =
        patch.stake !== undefined && nextRaw.length > 1 ? 'per_tip' : state.stake_mode;
      const next = normalizeTipStakes({
        tips: nextRaw,
        betMode: nextBetMode,
        stakeMode: nextStakeMode,
        systemRows: nextSystemRows,
      });
      return {
        ...state,
        ticket_slip: next,
        bet_mode: nextBetMode,
        stake_mode: nextStakeMode,
        system_rows: nextSystemRows,
        total_stake: deriveTotalStake({
          tips: next,
          betMode: nextBetMode,
          stakeMode: nextStakeMode,
          systemRows: nextSystemRows,
          previousTotalStake: state.total_stake,
        }),
      };
    });
  },

  setTips: (tips) => {
    const nextSystemRows = syncSystemRows(tips, []);
    const nextBetMode = hasActiveSystemRows(nextSystemRows) ? 'SYSTEM' : 'STANDARD';
    const nextStakeMode: TicketStakeMode = tips.length <= 1 ? 'total' : 'total';
    const nextTips = normalizeTipStakes({
      tips,
      betMode: nextBetMode,
      stakeMode: nextStakeMode,
      systemRows: nextSystemRows,
    });
    set((state) => ({
      ...state,
      ticket_slip: nextTips,
      total_stake: deriveTotalStake({
        tips: nextTips,
        betMode: nextBetMode,
        stakeMode: nextStakeMode,
        systemRows: nextSystemRows,
        previousTotalStake: state.total_stake,
      }),
      bet_mode: nextBetMode,
      stake_mode: nextStakeMode,
      system_rows: nextSystemRows,
    }));
  },

  setLoading: (loading) => set((state) => ({ ...state, loading })),
  setError: (error) => set((state) => ({ ...state, error })),

  setTotalStake: (totalStake) =>
    set((state) => ({
      ...state,
      stake_mode: 'total',
      total_stake: totalStake,
    })),

  setBetMode: (betMode) =>
    set((state) => {
      const nextBetMode =
        betMode === 'SYSTEM' && hasActiveSystemRows(state.system_rows) ? 'SYSTEM' : 'STANDARD';
      return {
        ...state,
        bet_mode: nextBetMode,
        total_stake: deriveTotalStake({
          tips: state.ticket_slip,
          betMode: nextBetMode,
          stakeMode: state.stake_mode,
          systemRows: state.system_rows,
          previousTotalStake: state.total_stake,
        }),
      };
    }),

  setSystemRowStake: (size, stakePerLine) =>
    set((state) => {
      const nextSystemRows = state.system_rows.map((row) =>
        row.size === size
          ? { ...row, stake_per_line: Number.isFinite(stakePerLine) ? stakePerLine : 0 }
          : row
      );
      const nextBetMode = hasActiveSystemRows(nextSystemRows) ? 'SYSTEM' : 'STANDARD';
      return {
        ...state,
        bet_mode: nextBetMode,
        system_rows: nextSystemRows,
        total_stake: deriveTotalStake({
          tips: state.ticket_slip,
          betMode: nextBetMode,
          stakeMode: state.stake_mode,
          systemRows: nextSystemRows,
          previousTotalStake: state.total_stake,
        }),
      };
    }),

  setBetslipTab: (tab) => set((state) => ({ ...state, betslipTab: tab })),
  setNewlyPlacedTicketId: (id) => set((state) => ({ ...state, newlyPlacedTicketId: id })),

  recomputeTotalStake: () => {
    const { ticket_slip, bet_mode, stake_mode, system_rows } = get();
    set((state) => ({
      ...state,
      total_stake: deriveTotalStake({
        tips: ticket_slip,
        betMode: bet_mode,
        stakeMode: stake_mode,
        systemRows: system_rows,
        previousTotalStake: state.total_stake,
      }),
    }));
  },

  pruneTipsByEventIds: (eventIds) => {
    const normalized = eventIds.map((id) => String(id));
    set((state) => {
      const nextRaw = state.ticket_slip.filter((tip) => normalized.includes(String(tip.event_id)));
      const nextSystemRows = syncSystemRows(nextRaw, state.system_rows);
      const nextBetMode = hasActiveSystemRows(nextSystemRows) ? 'SYSTEM' : 'STANDARD';
      const nextStakeMode: TicketStakeMode =
        nextRaw.length <= 1 ? 'total' : state.stake_mode;
      const next = normalizeTipStakes({
        tips: nextRaw,
        betMode: nextBetMode,
        stakeMode: nextStakeMode,
        systemRows: nextSystemRows,
      });
      return {
        ...state,
        ticket_slip: next,
        bet_mode: nextBetMode,
        stake_mode: nextStakeMode,
        system_rows: nextSystemRows,
        total_stake: deriveTotalStake({
          tips: next,
          betMode: nextBetMode,
          stakeMode: nextStakeMode,
          systemRows: nextSystemRows,
          previousTotalStake: state.total_stake,
        }),
      };
    });
  },
}));

